import { useDialog } from 'features/modalManager';
import SupplyChainImg from 'img/supply-chain.png';
import { t, Trans } from '@lingui/macro';
import { Copy } from '@phosphor-icons/react';

import { useTheme } from '@emotion/react';
import { useNotification } from 'features/notifications';
import { useEvent } from 'lib/segment';
import { useUser } from 'features/auth';
import { IconButton } from 'components/button/IconButton';

export const useDialogs = () => {
  const { openDialog } = useDialog();
  const theme = useTheme();
  const { notify } = useNotification();
  const event = useEvent();
  const { data: userData } = useUser();

  const handleCopyInviteCodeFromDialog = async (inviteCode: string) => {
    if (inviteCode && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(inviteCode);
      notify.info({
        message: t`Copied to clipboard!`,
        id: 'invite-code-copy',
        noDuplicate: true,
      });
    }
  };

  const inviteCodeDialog = (inviteCode: string) => {
    event('Supplier Portal Invite Code Viewed', {
      eventType: 'View',
      objectType: 'Page',
      targetType: 'Invite Code',
      businessContext: { inviteCode, organizationName: userData?.loginContext?.organizationName },
      source: 'Navigation',
      page: 'Supplier Portal Dashboard',
    });
    event('Welcome Message Displayed', {
      eventType: 'View',
      objectType: 'Modal',
      targetType: 'Welcome Message',
      source: 'System',
      page: 'Supplier Portal Dashboard',
      businessContext: {
        inviteCode,
        organizationId: userData?.loginContext?.organizationId,
        showAgain: localStorage.getItem('invitecode-welcome-back-showAgain') || true,
      },
    });
    // Text component has a recursion issue inside dialogs, hence using the inline styling
    openDialog({
      id: 'invitecode-welcome',
      corner: true,
      modalColor: 'primary',
      image: SupplyChainImg,
      title: 'Welcome!',
      titleProps: { variant: 'displaySm', weight: 'bold', fontFamily: 'Gazpacho' },
      children: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '8px' }}>
          <div
            style={{
              fontSize: theme.typography.bodySm.fontSize,
              color: theme.palette.text.primaryOnBrand,
              marginRight: '48px',
            }}
          >
            <Trans>
              Welcome to our enhanced supplier portal! This unified account lets you manage all your
              customer accounts in one place. If you have existing Delogue accounts, please ask your
              customers to convert them to this new account using the admin panel and the invite
              code below:
            </Trans>
          </div>
          <div
            style={{
              fontSize: theme.typography.bodyXs.fontSize,
              fontWeight: theme.typography.weight.semiBold,
              color: theme.palette.text.secondaryOnBrand,
            }}
          >
            <Trans>Invite code will be displayed in the top right</Trans>
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center' }}>
            <div
              style={{
                fontWeight: theme.typography.weight.semiBold,
                color: theme.palette.text.primaryOnBrand,
                fontSize: theme.typography.bodyMd.fontSize,
              }}
            >
              {inviteCode}
            </div>
            <IconButton
              size="lg"
              onClick={() => handleCopyInviteCodeFromDialog(inviteCode)}
              title={t`Copy invite code`}
            >
              <Copy size={24} color={theme.palette.text.primaryOnBrand} />
            </IconButton>
          </div>
        </div>
      ),
      showAgainCheckbox: 'invitecode-welcome-back',
    });
  };

  return { inviteCodeDialog };
};
